import { useState, useEffect } from 'react';
import { getAccessToken } from '../utils/auth';
import Editor from '@monaco-editor/react';

function DatasourceEdit({ datasourceId, onCancel, onUnsavedChange }) {
  const datasource = { id: datasourceId };
  const [config, setConfig] = useState({ display_name: '', description: '', type: '', params: {}});
  const [types, setTypes] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [scanResults, setScanResults] = useState([]);
  const [schema, setSchema] = useState({});
  const [schemaValues, setSchemaValues] = useState({});
  const [scanSortConfig, setScanSortConfig] = useState({ key: null, direction: 'asc' });
  const [pathFilter, setPathFilter] = useState('');
  const [selectedContent, setSelectedContent] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModified, setIsModified] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
  };

  const formatNumber = (num) => {
    return Number(num).toLocaleString();
  };

  const handleScanNow = async () => {
    const token = await getAccessToken();
    if (token) {
      try {
        const response = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource/${datasource.id}/scan`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (response.ok) {
          setScanResults(data['content']);
        } else {
          setModalMessage(data.message || 'An error occurred while scanning');
          setShowModal(true);
        }
      } catch (error) {
        console.error('Error scanning datasource:', error);
        setModalMessage('Network error occurred while scanning');
        setShowModal(true);
      }
    }
  };

  const handleScanSort = (key) => {
    const direction = scanSortConfig.key === key && scanSortConfig.direction === 'asc' ? 'desc' : 'asc';
    setScanSortConfig({ key, direction });
  };

  const handleRowClick = async (item) => {
    const token = await getAccessToken();

    // console.log('Fetching content for item:', item);

    if (token) {
      // const queryParam = item.sha ? `sha=${item.sha}` : `path=${item.path}`;
      const queryParam = `sha=${item.sha}&path=${item.path}`;
      fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource/${datasource.id}/content?${queryParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => response.json())
        .then(data => {
          setSelectedContent(data.content);
          setSelectedItem(item);
        })
        .catch(error => console.error('Error fetching content:', error));
    }
  };

  const closeEditor = () => {
    setSelectedContent(null);
    setSelectedItem(null);
  };

  const getLanguage = (path) => {
    if (!path) return 'text';
    if (path.endsWith('.md') || path.endsWith('.mdx')) return 'markdown';
    if (path.endsWith('.json')) return 'json';
    return 'text';
  };

  const handleAuthorize = async () => {
    const token = await getAccessToken();
    if (token) {
      fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource/${datasource.id}/authorize`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => response.text())
        .then(authorizeUrl => {
          console.log('Authorize URL:', authorizeUrl);
          const cleanUrl = authorizeUrl.replace(/^"(.*)"$/, '$1');
          const popup = window.open(
            cleanUrl,
            'authorize',
            'width=800,height=600,left=' + (window.screen.width / 2 - 400) + ',top=' + (window.screen.height / 2 - 300)
          );
          
          const checkClosed = setInterval(() => {
            try {
              if (popup.location.href === 'http://localhost:3000/close') {
                popup.close();
                clearInterval(checkClosed);
              }
            } catch (error) {
              // Cross-origin error, continue checking
            }
            
            if (popup.closed) {
              clearInterval(checkClosed);
            }
          }, 1000);
        })
        .catch(error => console.error('Error fetching authorize URL:', error));
    }
  };

  const handleSave = async () => {
    const token = await getAccessToken();
    if (token) {
      const params = {};
      Object.keys(schema).forEach(key => {
        const value = schemaValues[key] || '';
        if (['include_glob', 'include_paths', 'exclude_paths'].includes(key)) {
          params[key] = value ? (value.includes(',') ? value.split(',').map(v => v.trim()) : [value]) : [];
        } else {
          params[key] = value;
        }
      });
      
      const payload = {
        display_name: config.display_name,
        name: config.display_name,
        description: config.description,
        type: config.type,
        params
      };
      
      const isNewDatasource = datasource.id === "0";
      const url = isNewDatasource 
        ? `${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource`
        : `${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource/${datasource.id}`;
      const method = isNewDatasource ? 'POST' : 'PATCH';
      
      try {
        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        
        if (response.ok) {
          if (isNewDatasource && data.id) {
            const newResponse = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource/${data.id}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            const newData = await newResponse.json();
            setConfig(newData);
            datasource.id = data.id;
          }
          setIsModified(false);
          if (onUnsavedChange) onUnsavedChange(false);
          setModalMessage('Configuration saved successfully');
          setShowModal(true);
        } else {
          setModalMessage(`Cannot save changes at this time${data.message ? ': ' + data.message : ''}`);
          setShowModal(true);
        }
      } catch (error) {
        console.error('Error saving datasource:', error);
        setModalMessage('Cannot save changes at this time: Network error occurred');
        setShowModal(true);
      }
    }
  };

  const handleCancel = () => {
    if (isModified) {
      setShowCancelModal(true);
    } else {
      onCancel();
    }
  };

  const filteredResults = pathFilter.length >= 3 
    ? scanResults.filter(item => item.path?.toLowerCase().includes(pathFilter.toLowerCase()))
    // ? scanResults.filter(item => item.name?.toLowerCase().includes(pathFilter.toLowerCase())) // filter by name instead of path
    : scanResults;

  const sortedScanResults = [...filteredResults].sort((a, b) => {
    if (!scanSortConfig.key) return 0;
    
    if (scanSortConfig.key === 'size') {
      const aValue = parseFloat(a[scanSortConfig.key]) || 0;
      const bValue = parseFloat(b[scanSortConfig.key]) || 0;
      return scanSortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    } else {
      const aValue = a[scanSortConfig.key]?.toString().toLowerCase() || '';
      const bValue = b[scanSortConfig.key]?.toString().toLowerCase() || '';
      return scanSortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
  });

  const fetchSchema = async (type, params = {}) => {
    if (!type) {
      setSchema({});
      setSchemaValues({});
      return;
    }
    const token = await getAccessToken();
    if (token) {
      fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource/type/${type}/schema`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => response.json())
        .then(data => {
          setSchema(data['schema']);
          const initialValues = {};
          Object.keys(data['schema']).forEach(key => {
            const value = params[key] || '';
            initialValues[key] = Array.isArray(value) ? value.join(', ') : value;
          });
          setSchemaValues(initialValues);
        })
        .catch(error => console.error('Error fetching schema:', error));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const token = await getAccessToken();
      if (token) {
        if (datasource.id !== "0") {
          fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource/${datasource.id}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
            .then(response => response.json())
            .then(data => {
              setConfig(data);
              if (data.type) {
                fetchSchema(data.type, data.params);
              }
            })
            .catch(error => console.error('Error fetching datasource config:', error));
        }
        
        fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/datasource/types`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
          .then(response => response.json())
          .then(data => setTypes(data.types))
          .catch(error => console.error('Error fetching datasource types:', error));
      }
    };
    fetchData();
  }, [datasource.id]);

  return (
    <div className='edit-page'>
      <div className="header-layout">
        <h2 className="header-title">Edit Data Source</h2>
      </div>
      <div className="edit-form">
        <div className="edit-form-layout">
          <div className="edit-form-column-1">
            <div className="form-field">
              <label className="form-label">Display Name</label>
              <input 
                type="text" 
                value={config.display_name} 
                onChange={(e) => {
                  setConfig({...config, display_name: e.target.value});
                  setIsModified(true);
                  if (onUnsavedChange) onUnsavedChange(true);
                }}
                className="form-input"
              />
            </div>
            <div className="form-field">
              <label className="form-label">Description</label>
              <textarea 
                value={config.description} 
                onChange={(e) => {
                  setConfig({...config, description: e.target.value});
                  setIsModified(true);
                  if (onUnsavedChange) onUnsavedChange(true);
                }}
                className="form-input"
                rows="5"
              />
            </div>
            <div className="form-field dropdown">
              <label className="form-label">Type</label>
              <div 
                onClick={() => setShowDropdown(!showDropdown)}
                className="dropdown-trigger"
              >
                {config.type ? (
                  <>
                    <img src={`/media/${config.type?.toLowerCase()}-icon.png`} alt={config.type} className="icon-small" />
                    {config.type}
                  </>
                ) : 'Select a type'}
              </div>
              {showDropdown && (
                <div className="dropdown-menu">
                  {types.map((type, index) => (
                    <div 
                      key={index} 
                      onClick={() => { 
                        setConfig({...config, type}); 
                        setShowDropdown(false); 
                        fetchSchema(type);
                        setIsModified(true);
                        if (onUnsavedChange) onUnsavedChange(true);
                      }}
                      className="dropdown-option"
                    >
                      <img src={`/media/${type.toLowerCase()}-icon.png`} alt={type} className="icon-small" />
                      {type}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-field authorize-field">
              <button className="btn-save" onClick={handleAuthorize}>
                Authorize
              </button>
            </div>
            <div className="dynamic-form-fields">
              {Object.keys(schema).map((key) => (
                <div key={key} className="form-field">
                  <label className="form-label">{key}</label>
                  <input 
                    type="text" 
                    value={schemaValues[key] || ''}
                    onChange={(e) => {
                      setSchemaValues({...schemaValues, [key]: e.target.value});
                      setIsModified(true);
                      if (onUnsavedChange) onUnsavedChange(true);
                    }}
                    className="form-input"
                  />
                </div>
              ))}
            </div>
            <div className="form-field">
              <div className="info-box">
                <div><strong>Created:</strong> {formatDate(config.created)}</div>
                <div><strong>Modified:</strong> {formatDate(config.modified)}</div>
                <div><strong>ID:</strong> {datasourceId}</div>
              </div>
            </div>
            <div className="button-group">
              <button 
                onClick={handleCancel}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button className="btn-save" onClick={handleSave} disabled={!isModified}>
                Save
              </button>
            </div>
          </div>
          <div className="content-metadata">
            <div className="scan-controls">
              <button className="btn-save" onClick={handleScanNow}>
                Scan Now
              </button>
              {scanResults.length > 0 && (
                <input
                  type="text"
                  placeholder="Filter by path (min 3 chars)"
                  value={pathFilter}
                  onChange={(e) => setPathFilter(e.target.value)}
                  className="form-input filter-input"
                />
              )}
              {scanResults.length > 0 && (
                <div className="datasource-edit-total-content">
                  Total content: {filteredResults.length.toLocaleString()}
                </div>
              )}
            </div>
            {selectedContent ? (
              <div className="datasource-edit-editor-container">
                <div className="datasource-edit-editor-wrapper">
                <button 
                  onClick={closeEditor}
                  className="datasource-edit-close-btn"
                >
                  ✕
                </button>
                <Editor
                  height="calc(100vh - 120px)"
                  defaultLanguage={getLanguage(selectedItem?.path)}
                  value={selectedContent}
                  options={{ readOnly: true, lineNumbers: 'off', minimap: { enabled: false }, wordWrap: 'on'}}
                />
                </div>
              </div>
            ) : scanResults.length > 0 && (
              <table className="data-table scan-table">
                <thead>
                  <tr>
                    <th onClick={() => handleScanSort('name')} className="datasource-edit-scan-table-header">Name</th>
                    {/* <th onClick={() => handleScanSort('sha')} className="datasource-edit-scan-table-header">Path</th> */}
                    <th onClick={() => handleScanSort('size')} className="datasource-edit-scan-table-header">Size</th>
                    <th onClick={() => handleScanSort('ID')} className="datasource-edit-scan-table-header">ID</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedScanResults.map((item, index) => (
                    <tr key={index} onClick={() => handleRowClick(item)} className="datasource-edit-scan-table-row">
                      <td>{item.name}</td>
                      {/* <td>{item.sha}</td> */}
                      <td>{formatNumber(item.size)}</td>
                      <td>{item.path}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-compact">
            <div dangerouslySetInnerHTML={{ __html: modalMessage }} />
            <div className="modal-buttons">
              <button onClick={() => setShowModal(false)} className="btn-save">Close</button>
            </div>
          </div>
        </div>
      )}
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-compact">
            <h3>Unsaved changes</h3>
            <p>You have unsaved changes. Are you sure you want to leave without saving?</p>
            <div className="modal-buttons">
              <button onClick={() => setShowCancelModal(false)} className="btn-cancel">Stay</button>
              <button onClick={() => { setShowCancelModal(false); onCancel(); }} className="btn-save">Leave</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatasourceEdit;